import type { Event, Organization, WebSite, WithContext } from 'schema-dts';
import type { FirstSectionProps } from '@/shared/components/sections/FirstSection';
import { ddMmYyyyToIso8601Date } from '@/shared/lib/utils/dates';
import {
    type SchemaContacts,
    buildSchemaOrganization,
    buildSchemaPlace,
    oneLine,
    schemaEventImage,
    schemaIdRef,
    stripHtml,
} from '@/shared/lib/utils/schema-common';
import {
    SCHEMA_EVENT_ATTENDANCE,
    SCHEMA_EVENT_STATUS,
    SCHEMA_FORUM_EVENT_NAME,
    SCHEMA_ORGANIZATION_NAME,
    getSchemaIds,
    getSchemaSiteUrl,
} from '@/shared/lib/utils/schema-ids';

interface EventDates {
    startDay: string;
    endDay: string;
}

interface IndexJsonLdOptions {
    firstSectionProps?: FirstSectionProps;
    eventDates?: EventDates | null;
    contacts?: SchemaContacts | null;
    metaDescription?: string | null;
}

export function generateIndexJsonLd({
    firstSectionProps,
    eventDates,
    contacts,
    metaDescription,
}: IndexJsonLdOptions): WithContext<Organization | WebSite | Event>[] {
    const ids = getSchemaIds();
    const siteUrl = getSchemaSiteUrl();
    const organization = buildSchemaOrganization(contacts);

    const website: WithContext<WebSite> = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': ids.website,
        url: siteUrl,
        inLanguage: 'ru',
        name: SCHEMA_ORGANIZATION_NAME,
        publisher: schemaIdRef(ids.organization) as WebSite['publisher'],
    };

    const startDateIso = eventDates?.startDay ? ddMmYyyyToIso8601Date(eventDates.startDay) : undefined;
    const endDateIso = eventDates?.endDay ? ddMmYyyyToIso8601Date(eventDates.endDay) : undefined;
    const eventImage = schemaEventImage(firstSectionProps?.poster?.src);
    const name =
        oneLine(`${firstSectionProps?.title ?? ''} ${firstSectionProps?.info ?? ''}`) || SCHEMA_FORUM_EVENT_NAME;
    const description = oneLine(stripHtml(metaDescription || firstSectionProps?.text || ''));

    const event: WithContext<Event> = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        '@id': ids.forum,
        name,
        url: siteUrl,
        ...(description ? { description } : {}),
        ...(startDateIso ? { startDate: startDateIso } : {}),
        ...(endDateIso ? { endDate: endDateIso } : {}),
        eventStatus: SCHEMA_EVENT_STATUS,
        eventAttendanceMode: SCHEMA_EVENT_ATTENDANCE,
        ...(eventImage ? { image: eventImage } : {}),
        location: buildSchemaPlace(contacts),
        organizer: schemaIdRef(ids.organization) as Event['organizer'],
        subEvent: [schemaIdRef(ids.tnfForum), schemaIdRef(ids.tnfExpo)] as Event['subEvent'],
    };

    return [organization, website, event];
}
